import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RotateCcw, Star, Link2, Inbox, ExternalLink } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { LEAD_ENQUIRIES_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { getStageBadgeClass } from "../../utils/leadStageBadge";
import { formatDate, formatTime } from "../../utils/datetime";

// A lead's enquiries and the other leads on its number — trustedge
// customer/enquiries/ (customers/dedupe_api.py).
//
//   <LeadReturnBanner customerId />   left column, only when there is something to say
//   <LeadEnquiriesPanel customerId /> the Enquiries tab

const OUTCOME_STYLE = {
    created: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
    created_linked: "bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300",
    linked_new: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300",
    attached: "bg-brand-50 text-brand-700 dark:bg-brand-900/20 dark:text-brand-300",
    repeat: "bg-gray-50 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
    reopened: "bg-brand-600 text-white",
    merged: "bg-sky-50 text-sky-700 dark:bg-sky-900/20 dark:text-sky-300",
};

function useLeadEnquiries(customerId, refreshKey) {
    const { fetch } = useApiMethods();
    const [data, setData] = useState(null);
    useEffect(() => {
        if (!customerId) { setData(null); return; }
        let cancelled = false;
        fetch(LEAD_ENQUIRIES_URL, { customer_id: customerId })
            .then(res => { if (!cancelled && res?.status === 'success') setData(res); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [customerId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
    return data;
}

function useOpenLead() {
    const navigate = useNavigate();
    return (id) => navigate(`/customers/customerdetails?id=${id}`, { state: { formEdit: true, row: { id } } });
}

export function LeadReturnBanner({ customerId, refreshKey, onOpenEnquiries }) {
    const data = useLeadEnquiries(customerId, refreshKey);
    const openLead = useOpenLead();
    if (!data) return null;
    const returns = (data.enquiry_count || 1) > 1;
    const rel = data.related_customer;
    const linked = data.linked || [];
    if (!returns && !rel && !linked.length) return null;
    const last = (data.enquiries || []).find(e => e.outcome !== 'repeat');

    return (
        <div className="mt-3 rounded-xl border border-brand-200 dark:border-brand-800/50 bg-brand-50/60 dark:bg-brand-900/10 p-3 space-y-1.5 text-xs">
            {returns && (
                <p className="flex items-center gap-1.5 font-semibold text-brand-800 dark:text-brand-200">
                    <RotateCcw className="h-3.5 w-3.5" />
                    Came back {data.enquiry_count}×
                    {data.last_enquiry_at && (
                        <span className="font-normal text-brand-700/80 dark:text-brand-300/80">
                            · last {formatDate(data.last_enquiry_at)}{last?.source ? ` via ${last.source}` : ''}
                        </span>
                    )}
                </p>
            )}
            {rel && (
                <p className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
                    {rel.state === 'customer' ? <Star className="h-3.5 w-3.5 text-emerald-600" /> : <Link2 className="h-3.5 w-3.5 text-amber-600" />}
                    {rel.state === 'customer' ? 'Existing customer: ' : 'Linked to: '}
                    <button type="button" onClick={() => openLead(rel.id)} className="font-medium text-primary hover:underline truncate">
                        {rel.customer_name}
                    </button>
                    <span className={cn("px-1.5 py-0.5 rounded-full text-[9px] font-medium", getStageBadgeClass(rel.lead_stage))}>{rel.lead_stage}</span>
                </p>
            )}
            {!rel && linked.length > 0 && (
                <p className="flex items-center gap-1.5 text-amber-800 dark:text-amber-300">
                    <Link2 className="h-3.5 w-3.5" />
                    {linked.length} other lead{linked.length === 1 ? '' : 's'} on this number -
                    {/* The tab sits mid-way along a scrolling strip of a dozen,
                        often off screen, so take the user there rather than
                        tell them to go looking. */}
                    {onOpenEnquiries
                        ? <button type="button" onClick={onOpenEnquiries} className="font-medium underline hover:no-underline">view them</button>
                        : <span>see the Enquiries tab</span>}
                </p>
            )}
            {data.aliases?.length > 0 && (
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    Also known as: {data.aliases.join(', ')}
                </p>
            )}
        </div>
    );
}

export default function LeadEnquiriesPanel({ customerId, refreshKey }) {
    const data = useLeadEnquiries(customerId, refreshKey);
    const openLead = useOpenLead();
    if (!data) {
        return <p className="text-xs text-gray-400 py-6 text-center">Loading enquiries…</p>;
    }
    const enquiries = data.enquiries || [];
    const linked = data.linked || [];

    return (
        <div className="space-y-5">
            <section>
                <h3 className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2">
                    <Inbox className="h-3.5 w-3.5" /> Enquiries
                    <span className="font-normal text-gray-400">· {data.enquiry_count || 1} from this person</span>
                </h3>
                {enquiries.length === 0 ? (
                    <p className="text-xs text-gray-400 py-3">
                        No enquiries recorded yet. Leads created before duplicate tracking started show their
                        first one here only once they enquire again.
                    </p>
                ) : (
                    <ol className="relative border-l border-gray-200 dark:border-gray-700 ml-1.5 space-y-3">
                        {enquiries.map(e => (
                            <li key={e.id} className="ml-4">
                                <span className="absolute -left-1.5 mt-1 h-3 w-3 rounded-full border-2 border-white dark:border-gray-800 bg-brand-400" />
                                <div className="flex flex-wrap items-center gap-1.5 text-xs">
                                    <span className="font-medium text-gray-800 dark:text-gray-100">
                                        {formatDate(e.received_at)} <span className="text-gray-400 font-normal">{formatTime(e.received_at)}</span>
                                    </span>
                                    <span className="text-gray-600 dark:text-gray-300">{e.source || 'Unknown source'}</span>
                                    {e.form_name && <span className="text-gray-400">· {e.form_name}</span>}
                                    <span className={cn("px-1.5 py-0.5 rounded text-[10px] font-medium", OUTCOME_STYLE[e.outcome] || OUTCOME_STYLE.created)}>
                                        {e.outcome_label}
                                        {e.outcome === 'reopened' && e.previous_stage ? ` (was ${e.previous_stage})` : ''}
                                    </span>
                                </div>
                                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                    {[e.name, e.phone, e.email].filter(Boolean).join(' · ')}
                                    {e.ad_name ? ` · ad: ${e.ad_name}` : ''}
                                    {e.logged_by ? ` · logged by ${e.logged_by}` : ''}
                                </p>
                                {e.message && (
                                    <p className="mt-1 text-[11px] text-gray-600 dark:text-gray-300 whitespace-pre-line bg-gray-50 dark:bg-gray-700/40 rounded px-2 py-1.5">
                                        {e.message}
                                    </p>
                                )}
                            </li>
                        ))}
                    </ol>
                )}
            </section>

            <section>
                <h3 className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2">
                    <Link2 className="h-3.5 w-3.5" /> Other leads on this number
                </h3>
                {linked.length === 0 ? (
                    <p className="text-xs text-gray-400">None - this is the only lead with this number.</p>
                ) : (
                    <div className="space-y-1.5">
                        {linked.map(m => (
                            <div key={m.id} className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-xs">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        {m.state === 'customer' && <Star className="h-3 w-3 text-emerald-600" />}
                                        <span className="font-medium text-gray-800 dark:text-gray-100 truncate max-w-[220px]">{m.customer_name || '—'}</span>
                                        <span className={cn("px-1.5 py-0.5 rounded-full text-[9px] font-medium", getStageBadgeClass(m.lead_stage))}>{m.lead_stage}</span>
                                    </div>
                                    <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                                        {m.owner ? `Owner ${m.owner}` : 'Unassigned'}
                                        {m.lead_source ? ` · ${m.lead_source}` : ''}
                                        {m.created_date ? ` · created ${formatDate(m.created_date)}` : ''}
                                    </p>
                                </div>
                                {m.can_open ? (
                                    <button type="button" onClick={() => openLead(m.id)}
                                            className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline flex-shrink-0">
                                        Open <ExternalLink className="h-3 w-3" />
                                    </button>
                                ) : <span className="text-[10px] text-gray-400 flex-shrink-0">Not yours</span>}
                            </div>
                        ))}
                        <p className="text-[10px] text-gray-400">
                            Same person twice? An admin can merge them in Settings → Duplicate Rules.
                        </p>
                    </div>
                )}
            </section>
        </div>
    );
}
